import { jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
import { Box, Link, Table, Text } from '@chakra-ui/react';
import ReactMarkdown from 'react-markdown';
import { memo, useMemo } from 'react';
import remarkGfm from 'remark-gfm';
import { remarkCreatorMentions } from './remark-creator-mentions';
function buildComponents(compact) {
    const baseFontSize = compact ? '13px' : '15px';
    const smallFontSize = compact ? '12px' : 'sm';
    return {
        p: ({ children }) => (_jsx(Text, { fontSize: baseFontSize, lineHeight: "tall", mb: 2, _last: { mb: 0 }, children: children })),
        strong: ({ children }) => (_jsx(Text, { as: "span", fontWeight: "semibold", children: children })),
        code: ({ children, className }) => {
            const isBlock = className?.startsWith('language-');
            if (isBlock) {
                return (_jsx(Box, { as: "pre", overflowX: "auto", bg: "gray.100", p: 3, borderRadius: "md", fontSize: smallFontSize, fontFamily: "mono", whiteSpace: "pre-wrap", wordBreak: "break-word", children: _jsx("code", { children: children }) }));
            }
            return (_jsx(Text, { as: "span", fontFamily: "mono", fontSize: smallFontSize, bg: "gray.100", px: 1, borderRadius: "sm", children: children }));
        },
        pre: ({ children }) => _jsx(_Fragment, { children: children }),
        a: ({ href, children }) => (_jsx(Link, { href: href, target: "_blank", rel: "noopener noreferrer", color: "blue.500", textDecoration: "underline", children: children })),
        table: ({ children }) => (_jsx(Box, { overflowX: "auto", w: "full", my: 1, ...(compact && {
                fontSize: '12px',
                borderRadius: 'md',
                overflow: 'hidden',
            }), children: _jsx(Table.Root, { size: "sm", variant: "outline", ...(compact && { w: 'max-content', minW: 'full' }), children: children }) })),
        thead: ({ children }) => _jsx(Table.Header, { children: children }),
        tbody: ({ children }) => _jsx(Table.Body, { children: children }),
        tr: ({ children }) => _jsx(Table.Row, { children: children }),
        th: ({ children }) => (_jsx(Table.ColumnHeader, { ...(compact && {
                px: 2,
                py: 1,
                fontSize: 'xs',
                whiteSpace: 'nowrap',
            }), children: children })),
        td: ({ children }) => (_jsx(Table.Cell, { ...(compact && {
                px: 2,
                py: 1,
                fontSize: 'xs',
                wordBreak: 'break-word',
                whiteSpace: 'normal',
            }), children: children })),
        ul: ({ children }) => (_jsx(Box, { as: "ul", pl: 5, fontSize: baseFontSize, lineHeight: "tall", children: children })),
        ol: ({ children }) => (_jsx(Box, { as: "ol", pl: 5, fontSize: baseFontSize, lineHeight: "tall", listStyleType: "decimal", children: children })),
        li: ({ children }) => (_jsx(Box, { as: "li", pb: 1, children: children })),
        blockquote: ({ children }) => (_jsx(Box, { pl: 4, borderLeftWidth: "3px", borderLeftColor: "gray.300", color: "gray.600", fontStyle: "italic", children: children })),
        h1: ({ children }) => (_jsx(Text, { as: "h3", fontSize: compact ? 'md' : 'lg', fontWeight: "semibold", mt: 2, children: children })),
        h2: ({ children }) => (_jsx(Text, { as: "h4", fontSize: compact ? 'sm' : 'md', fontWeight: "semibold", mt: 2, children: children })),
        h3: ({ children }) => (_jsx(Text, { as: "h5", fontSize: compact ? 'xs' : 'sm', fontWeight: "semibold", mt: 1, children: children })),
    };
}
// Memoize components for each mode to avoid recreation on every render
const defaultComponents = buildComponents(false);
const compactComponents = buildComponents(true);
// Plugin list when no mention rendering is requested (stable reference so the
// legacy render path stays byte-for-byte identical to before this feature).
const baseRemarkPlugins = [remarkGfm];
const mentionRemarkPlugins = [remarkGfm, remarkCreatorMentions];
/**
 * The `creator-mention` element name is produced by `remarkCreatorMentions`
 * via `data.hName`. React-markdown lower-cases custom hast tag names, so the
 * component key must match the lower-cased form.
 */
const CREATOR_MENTION_TAG = 'creator-mention';
/**
 * Renders markdown content with Chakra UI styling.
 * Supports GFM (GitHub Flavored Markdown) including tables, strikethrough, and task lists.
 *
 * @param content - The markdown string to render
 * @param compact - When true, uses smaller font sizes suitable for narrow containers
 * @param renderMention - Optional renderer for `@handle` creator mentions
 */
export const MarkdownRenderer = memo(({ content, compact = false, renderMention }) => {
    const baseComponents = compact ? compactComponents : defaultComponents;
    const remarkPlugins = renderMention ? mentionRemarkPlugins : baseRemarkPlugins;
    const components = useMemo(() => {
        if (!renderMention)
            return baseComponents;
        return {
            ...baseComponents,
            // Custom element emitted by remarkCreatorMentions; `handle` arrives via hProperties.
            [CREATOR_MENTION_TAG]: ({ handle }) => handle ? _jsx(_Fragment, { children: renderMention(handle) }) : null,
        };
    }, [baseComponents, renderMention]);
    return (_jsx(ReactMarkdown, { remarkPlugins: remarkPlugins, components: components, children: content }));
});
MarkdownRenderer.displayName = 'MarkdownRenderer';
export { remarkCreatorMentions };
